<script setup lang="ts">
import * as yup from 'yup'
import SmallModel from '@/components/Interface/Modal/ModelSizes/SmallModel.vue'
import { computed, reactive } from 'vue'
import { useGameStore } from '@/stores/gameStore.ts'
import Backend from '@/models/backend.ts'
import FormProcess from '@/models/formProcess.ts'
import FormInput from '@/components/Interface/Modal/Forms/FormInput.vue'
import FormGenericError from '@/components/Interface/Modal/Forms/FormGenericError.vue'
import { useFleetStore } from '@/stores/fleetStore.ts'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// Get the planet data
const gameStore = useGameStore()
const fleetStore = useFleetStore()
const fleetData = computed(() => fleetStore.findFleetById(gameStore.modalData.fleetId))

const backend = new Backend()
const formProcess = new FormProcess()

// Define the validation schema
const schema = yup.object().shape({
  fleetName: yup
    .string()
    .min(3, () => t('modals.validation.minChars', { min: 3 }))
    .max(32, () => t('modals.validation.maxChars', { max: 32 }))
    .matches(/[a-zA-Z]/, () => t('modals.validation.mustContainLetter'))
    .required(() => t('modals.validation.required'))
})

// Default form values
const formValues = reactive<{ [key: string]: string }>({
  fleetName: fleetData.value?.name || ''
})

// Form errors
const formErrors = reactive<{ [key: string]: string }>({
  generic: '',
  fleetName: ''
})

// Handle form submission
const submit = () => {
  schema
    .validate(formValues)
    .then(() => {
      formProcess.resetFormErrors(formErrors)
      backend
        .updateFleetName(gameStore.modalData.fleetId, formValues.fleetName)
        .then(() => formProcess.processBackendSuccessResponse())
        .catch((error) => formProcess.processBackendErrorResponse(error, formErrors))
    })
    .catch((error) => formProcess.processFrontendErrorResponse(error, formErrors))
}
</script>

<template>
  <SmallModel :title="t('modals.editFleetName.title')">
    <form method="post" @submit.prevent="submit" class="pl-4 pr-4 pt-4">
      <FormInput
        :label="t('modals.editFleetName.label')"
        v-model="formValues.fleetName"
        :error="formErrors.fleetName"
        :autoFocus="true"
      />
      <div class="flex justify-between py-2">
        <FormGenericError :error="formErrors.generic" />
        <ActionButton type="submit" class="hidden md:block">{{ t('common.actions.edit') }}</ActionButton>
      </div>
    </form>
  </SmallModel>
</template>

<style scoped lang="scss"></style>
